import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMedicationInput,
  UpdateMedicationInput,
} from "@shared/validation/medication.schema";
import type { MedicationFrequency } from "@shared/constants/frequency";
import { apiClient, ApiError } from "./client";

function toIsoStringSafe(value: Date | string | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export interface MedicationSummary {
  id: string;
  childId: string;
  name: string;
  dosage: string;
  frequency: MedicationFrequency;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const medicationsKey = (childId: string) =>
  ["children", childId, "medications"] as const;

export function useMedications(childId: string | undefined) {
  return useQuery<MedicationSummary[], ApiError>({
    queryKey: childId
      ? medicationsKey(childId)
      : ["children", "__none__", "medications"],
    queryFn: () => apiClient<MedicationSummary[]>(`/api/children/${childId}/medications`),
    enabled: Boolean(childId),
  });
}

interface MedicationCreateWireBody {
  name: string;
  dosage: string;
  frequency: MedicationFrequency;
  expiresAt: string | null;
  notes: string | null | undefined;
}

interface MedicationUpdateWireBody {
  name?: string;
  dosage?: string;
  frequency?: MedicationFrequency;
  expiresAt?: string | null;
  notes?: string | null;
}

export function useCreateMedication(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<MedicationSummary, ApiError, CreateMedicationInput>({
    mutationFn: (vars) => {
      const body: MedicationCreateWireBody = {
        name: vars.name,
        dosage: vars.dosage,
        frequency: vars.frequency,
        expiresAt: toIsoStringSafe(vars.expiresAt),
        notes: vars.notes ?? null,
      };
      return apiClient<MedicationSummary>(`/api/children/${childId}/medications`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicationsKey(childId) });
    },
  });
}

export function useUpdateMedication(childId: string, medicationId: string) {
  const queryClient = useQueryClient();
  return useMutation<MedicationSummary, ApiError, UpdateMedicationInput>({
    mutationFn: (vars) => {
      const body: MedicationUpdateWireBody = {};
      if (vars.name !== undefined) body.name = vars.name;
      if (vars.dosage !== undefined) body.dosage = vars.dosage;
      if (vars.frequency !== undefined) body.frequency = vars.frequency;
      if (vars.expiresAt !== undefined) body.expiresAt = toIsoStringSafe(vars.expiresAt);
      if (vars.notes !== undefined) body.notes = vars.notes;
      return apiClient<MedicationSummary>(
        `/api/children/${childId}/medications/${medicationId}`,
        { method: "PUT", body: JSON.stringify(body) },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicationsKey(childId) });
    },
  });
}

export function useDeleteMedication(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, string>({
    mutationFn: (medicationId) =>
      apiClient<{ deleted: true }>(
        `/api/children/${childId}/medications/${medicationId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicationsKey(childId) });
    },
  });
}
